import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useGraphExplorer } from "../hooks/useGraphExplorer";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { TOPIC_DISPLAY_LABELS } from "../filterLabels";
import type { GraphData } from "../types";
import { AccessibleNodeList } from "./AccessibleNodeList";
import { DetailDrawer } from "./DetailDrawer";
import { GraphCanvas } from "./GraphCanvas";
import { GraphControls } from "./GraphControls";
import { Icon } from "./Icon";
import { MobileHeader } from "./MobileHeader";
import { SearchBar } from "./SearchBar";
import { Sidebar } from "./Sidebar";

interface GraphExperienceProps {
  data: GraphData;
}

export function GraphExperience({ data }: GraphExperienceProps) {
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [hasSelectedNode, setHasSelectedNode] = useState(false);
  const reducedMotion = useReducedMotion();
  const explorer = useGraphExplorer(data, reducedMotion);
  const selectNode = explorer.selectNode;
  const handleNodeSelect = useCallback(
    (nodeId: string | null) => {
      if (nodeId !== null) setHasSelectedNode(true);
      selectNode(nodeId);
    },
    [selectNode],
  );
  const typeLabels = useMemo(
    () => new Map(data.ontology.nodeTypes.map((type) => [type.id, type.label] as const)),
    [data.ontology.nodeTypes],
  );
  const hasSearch = explorer.deferredQuery.trim().length > 0;
  const hasActiveFilters =
    hasSearch || explorer.activeTopics.size > 0 || explorer.activeTypes.size > 0;
  const listedNodes = hasSearch ? explorer.searchResults : explorer.filteredNodes;
  const activeFilterChips = [
    ...Array.from(explorer.activeTopics).map((topic) => ({
      id: `topic-${topic}`,
      label: TOPIC_DISPLAY_LABELS[topic] ?? topic,
      onClear: () => explorer.toggleTopic(topic),
    })),
    ...Array.from(explorer.activeTypes).map((type) => ({
      id: `type-${type}`,
      label: typeLabels.get(type) ?? type,
      onClear: () => explorer.toggleType(type),
    })),
  ];
  const { clearEdgeTypeHighlight, highlightedEdgeType } = explorer;

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || highlightedEdgeType === null) return;

      event.preventDefault();
      event.stopPropagation();
      clearEdgeTypeHighlight();
    };

    window.addEventListener("keydown", handleEscape, true);
    return () => window.removeEventListener("keydown", handleEscape, true);
  }, [clearEdgeTypeHighlight, highlightedEdgeType]);

  const closeSidebar = () => {
    explorer.setSidebarOpen(false);
    requestAnimationFrame(() => menuButtonRef.current?.focus());
  };

  return (
    <main className={`app-shell${explorer.selectedNode ? " has-detail" : ""}`}>
      <Sidebar
        activeTopics={explorer.activeTopics}
        activeTypes={explorer.activeTypes}
        edges={data.edges}
        edgeTypes={data.ontology.edgeTypes}
        highlightedEdgeType={explorer.highlightedEdgeType}
        lens={explorer.lens}
        meta={data.meta}
        mobileOpen={explorer.sidebarOpen}
        origins={data.ontology.origins}
        onClose={closeSidebar}
        onEdgeTypeToggle={explorer.toggleEdgeTypeHighlight}
        onLensChange={explorer.setLens}
        onTopicToggle={explorer.toggleTopic}
        onTypeToggle={explorer.toggleType}
        topicFacets={explorer.topicFacets}
        typeFacets={explorer.typeFacets}
      />

      <section className="graph-workspace" aria-label="세컨드 브레인 그래프">
        <MobileHeader
          menuButtonRef={menuButtonRef}
          menuOpen={explorer.sidebarOpen}
          onMenuOpen={() => explorer.setSidebarOpen(true)}
        />
        <SearchBar
          onChange={explorer.setQuery}
          onSelect={handleNodeSelect}
          query={explorer.query}
          results={explorer.searchResults}
          typeLabels={typeLabels}
        />
        <div aria-live="polite" className="mobile-scope-state">
          <span aria-hidden="true" className="scope-dot" />
          <span className="mobile-scope-label">
            공개 {data.meta.nodeCount.toLocaleString("ko-KR")} · 연결 {data.meta.edgeCount.toLocaleString("ko-KR")}
          </span>
          <span
            aria-label="민감한 내용은 포함되지 않습니다."
            role="img"
            title="민감한 내용은 포함되지 않습니다."
          >
            <Icon name="info" size={14} />
          </span>
          <div aria-label="활성 필터와 표시 노드 수" className="mobile-filter-summary">
            {activeFilterChips.map((filter) => (
              <button
                aria-label={`${filter.label} 필터 해제`}
                aria-pressed={true}
                className="active-filter-chip"
                key={filter.id}
                onClick={filter.onClear}
                type="button"
              >
                <span>{filter.label}</span>
                <span aria-hidden="true">×</span>
              </button>
            ))}
            {activeFilterChips.length > 0 && (
              <span aria-hidden="true" className="mobile-filter-divider">
                ·
              </span>
            )}
            <span
              aria-label={`${explorer.filteredNodes.length}개/${data.nodes.length}개 노드 표시`}
              className="mobile-filter-count"
            >
              {explorer.filteredNodes.length}개 노드
            </span>
          </div>
        </div>
        <GraphCanvas
          autoRotate={explorer.autoRotate}
          edges={explorer.filteredEdges}
          edgeTypes={data.ontology.edgeTypes}
          highlightedEdgeType={explorer.highlightedEdgeType}
          hasActiveFilters={hasActiveFilters}
          focusNodeIds={explorer.focusNodeIds}
          hasSearch={hasSearch}
          lens={explorer.lens}
          nodes={explorer.filteredNodes}
          nodeTypes={data.ontology.nodeTypes}
          origins={data.ontology.origins}
          onSelect={handleNodeSelect}
          onClearEdgeTypeHighlight={explorer.clearEdgeTypeHighlight}
          onReset={explorer.resetFilters}
          reducedMotion={reducedMotion}
          searchMatchIds={explorer.searchMatchIds}
          selectedNodeId={explorer.selectedNodeId}
          viewportCommand={explorer.viewportCommand}
        />
        <GraphControls
          autoRotate={explorer.autoRotate}
          hasSelectedNode={hasSelectedNode}
          listOpen={explorer.nodeListOpen}
          onAutoRotateChange={explorer.setAutoRotate}
          onListToggle={() => explorer.setNodeListOpen((open) => !open)}
          onReset={explorer.resetFilters}
          onZoomIn={() => explorer.sendViewportCommand("zoom-in")}
          onZoomOut={() => explorer.sendViewportCommand("zoom-out")}
          reducedMotion={reducedMotion}
          resultCount={listedNodes.length}
          totalCount={data.nodes.length}
        />
        <AccessibleNodeList
          nodes={listedNodes}
          onClose={() => explorer.setNodeListOpen(false)}
          onSelect={handleNodeSelect}
          open={explorer.nodeListOpen}
          selectedNodeId={explorer.selectedNodeId}
          typeLabels={typeLabels}
        />
      </section>

      <DetailDrawer
        edges={data.edges}
        edgeTypes={data.ontology.edgeTypes}
        node={explorer.selectedNode}
        nodeById={explorer.nodeById}
        nodeTypes={data.ontology.nodeTypes}
        origins={data.ontology.origins}
        onClose={() => explorer.selectNode(null)}
        onSelect={explorer.selectNode}
      />
    </main>
  );
}
